import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { formatDate } from '../../utils/dateFormat';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';

// Same labels as the portal Quotations list: SENT is waiting for the customer.
const STATUS_CONFIG = {
  DRAFT: { color: 'warning', label: 'Draft' },
  FINAL: { color: 'primary', label: 'Final' },
  SENT: { color: 'info', label: 'Awaiting you' },
  ACCEPTED: { color: 'success', label: 'Accepted' },
  REJECTED: { color: 'error', label: 'Rejected' },
  EXPIRED: { color: 'default', label: 'Expired' },
  CONVERTED: { color: 'secondary', label: 'Converted' }
};

const StatusChip = ({ status }) => {
  const config = STATUS_CONFIG[status] || { color: 'default', label: status };
  return <Chip label={config.label} color={config.color} size="small" />;
};

const FS = '0.8125rem';
const SECTION_TITLE_SX = { fontSize: '0.875rem', fontWeight: 600, mb: 1.5 };
const LABEL_SX = { fontSize: '0.75rem', color: 'text.secondary' };
const VALUE_SX = { fontSize: FS, fontWeight: 500 };
const BTN_SX = { textTransform: 'none', fontSize: FS };

const Section = ({ title, children, sx }) => (
  <Paper sx={{ p: 2, ...sx }}>
    {title && <Typography sx={SECTION_TITLE_SX}>{title}</Typography>}
    {children}
  </Paper>
);

const Field = ({ label, children }) => (
  <Box>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Box sx={VALUE_SX}>{children}</Box>
  </Box>
);

const TotalRow = ({ label, value, strong }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4, py: 0.25 }}>
    <Typography sx={{ fontSize: FS, fontWeight: strong ? 600 : 400, color: strong ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: strong ? '0.875rem' : FS, fontWeight: strong ? 700 : 500, color: strong ? 'primary.main' : 'text.primary' }}>{value}</Typography>
  </Box>
);

const formatCurrency = (value) => `AED ${parseFloat(value || 0).toFixed(2)}`;

const PortalQuotationDetail = ({ portalUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { quotationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchQuotation();
  }, [quotationId]);

  const fetchQuotation = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.getPortalQuotation(quotationId);
      setQuotation(response.data?.data || null);
    } catch (err) {
      console.error('Failed to fetch quotation:', err);
      setError('Failed to load quotation details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await ApiService.acceptPortalQuotation(quotationId);
      fetchQuotation(); // Refresh data
    } catch (err) {
      console.error('Failed to accept quotation:', err);
      alert('Failed to accept quotation. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await ApiService.rejectPortalQuotation(quotationId, rejectReason);
      setRejectDialogOpen(false);
      fetchQuotation(); // Refresh data
    } catch (err) {
      console.error('Failed to reject quotation:', err);
      alert('Failed to reject quotation. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const canTakeAction = quotation?.status === 'SENT' && portalUser?.role === 'ORG_ADMIN';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={<Button size="small" onClick={fetchQuotation} sx={BTN_SX}>Retry</Button>}
      >
        {error}
      </Alert>
    );
  }

  if (!quotation) {
    return (
      <Alert severity="warning">
        Quotation not found
      </Alert>
    );
  }

  const lineItems = quotation.line_items || quotation.lineItems || [];
  const number = quotation.quotation_number || quotation.draft_number || quotation.quotationNumber || quotation.draftNumber || '—';
  const terms = quotation.terms_and_conditions || quotation.termsAndConditions;

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Tooltip title="Back to Quotations">
          <IconButton size="small" onClick={() => navigate('/portal/quotations')}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>{number}</Typography>
        <StatusChip status={quotation.status} />
        {canTakeAction && (
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button
              color="error"
              size="small"
              startIcon={<RejectIcon fontSize="small" />}
              onClick={() => setRejectDialogOpen(true)}
              disabled={actionLoading}
              sx={BTN_SX}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<AcceptIcon fontSize="small" />}
              onClick={handleAccept}
              disabled={actionLoading}
              sx={BTN_SX}
            >
              Accept Quotation
            </Button>
          </Box>
        )}
      </Box>

      {canTakeAction && (
        <Alert severity="info" sx={{ mb: 2, fontSize: FS }}>
          This quotation is awaiting your response. Accept it to go ahead, or reject it with a reason.
        </Alert>
      )}

      {/* Details + Totals */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Section title="Quotation Details" sx={{ height: '100%' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Field label="Quotation Number">{number}</Field>
              <Field label="Date">{formatDate(quotation.quotation_date || quotation.quotationDate, '—')}</Field>
              <Field label="Valid Until">{formatDate(quotation.valid_until || quotation.validUntil, '—')}</Field>
              <Field label="Items">{lineItems.length}</Field>
            </Box>
          </Section>
        </Grid>
        <Grid item xs={12} md={4}>
          <Section title="Totals" sx={{ height: '100%' }}>
            <TotalRow label="Subtotal" value={formatCurrency(quotation.subtotal)} />
            <TotalRow label="VAT" value={formatCurrency(quotation.tax_total ?? quotation.taxTotal)} />
            <Divider sx={{ my: 0.75 }} />
            <TotalRow label="Grand Total" value={formatCurrency(quotation.grand_total ?? quotation.grandTotal)} strong />
          </Section>
        </Grid>
      </Grid>

      {/* Line Items */}
      <Paper sx={{ mb: 2 }}>
        <Typography sx={{ ...SECTION_TITLE_SX, px: 2, pt: 1.5, mb: 1 }}>Line Items</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Govt Charge</TableCell>
                <TableCell align="right">Service Charge</TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No line items</TableCell>
                </TableRow>
              ) : (
                lineItems.map((item, index) => {
                  const serviceChargeDisplay =
                    parseFloat(item.service_charge || 0) + parseFloat(item.markup_amount || 0);
                  const taxDisplay =
                    parseFloat(item.tax_value || 0) + parseFloat(item.tax_on_markup || 0);
                  return (
                    <TableRow key={item.id || index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{item.service_name}</TableCell>
                      <TableCell>{item.customer_name || '—'}</TableCell>
                      <TableCell align="right">{item.quantity || 1}</TableCell>
                      <TableCell align="right">{formatCurrency(item.govt_charge)}</TableCell>
                      <TableCell align="right">{formatCurrency(serviceChargeDisplay)}</TableCell>
                      <TableCell align="right">{formatCurrency(taxDisplay)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.line_total)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {(quotation.notes || terms) && (
        <Grid container spacing={2}>
          {quotation.notes && (
            <Grid item xs={12} md={terms ? 6 : 12}>
              <Section title="Notes" sx={{ height: '100%' }}>
                <Typography sx={{ fontSize: FS, whiteSpace: 'pre-line' }}>{quotation.notes}</Typography>
              </Section>
            </Grid>
          )}
          {terms && (
            <Grid item xs={12} md={quotation.notes ? 6 : 12}>
              <Section title="Terms & Conditions" sx={{ height: '100%' }}>
                <Typography sx={{ fontSize: FS, whiteSpace: 'pre-line' }}>{terms}</Typography>
              </Section>
            </Grid>
          )}
        </Grid>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Reject Quotation</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: FS, mb: 1.5 }}>
            Reason for rejecting this quotation (optional):
          </Typography>
          <TextField
            fullWidth
            multiline
            size="small"
            rows={3}
            placeholder="Enter reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ '& .MuiInputBase-root': { fontSize: FS } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setRejectDialogOpen(false)} sx={BTN_SX}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
            sx={BTN_SX}
          >
            {actionLoading ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalQuotationDetail;
